import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PiCalendarDuotone,
  PiFloppyDiskDuotone,
  PiImageDuotone,
  PiPencilSimpleDuotone,
  PiSignOutDuotone,
  PiSwordDuotone,
  PiTrophyDuotone,
  PiUserCircleDuotone,
  PiUserDuotone,
} from 'react-icons/pi';
import { useAuth } from '../app/providers/AuthContext';
import {
  getCurrentUserProfile,
  getUserProfile,
  updateCurrentUser,
} from '../api/userProfileService';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import StatCard from '../shared/common/StatCard';
import EmptyState from '../shared/common/EmptyState';
import Input from '../shared/common/Input';
import { SkeletonList } from '../shared/common/Skeleton';
import { publicUserName, safeUsername } from '../shared/utils/userDisplay';

const EDIT_SUCCESS_MESSAGE = 'Профиль обновлён';
const LOAD_ERROR_MESSAGE = 'Не удалось загрузить профиль. Попробуй обновить страницу.';
const SAVE_ERROR_MESSAGE = 'Не удалось сохранить профиль';
const DISPLAY_NAME_REQUIRED_MESSAGE = 'Укажи отображаемое имя';
const USERNAME_REQUIRED_MESSAGE = 'Укажи ник';
const DISPLAY_NAME_MAX_LENGTH = 255;
const USERNAME_MAX_LENGTH = 255;
const PHOTO_URL_MAX_LENGTH = 512;
const DEFAULT_USER_NAME = 'Пользователь';
const DEFAULT_ROOM_LIMIT_TEXT = 'Последние комнаты, к которым привязан профиль';

const initialForm = Object.freeze({
  displayName: '',
  username: '',
  photoUrl: '',
});

const normalizeForm = (profile) => ({
  displayName: profile?.displayName || '',
  username: profile?.username || '',
  photoUrl: profile?.photoUrl || '',
});

const getInitials = (name) => name.trim().slice(0, 2).toUpperCase() || 'BM';

const messageFromError = (error, fallback) => (
  error?.response?.data?.message || fallback
);

const formatDate = (date) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user, logout, setUser, refreshCurrentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState('');

  const parsedUserId = userId ? Number(userId) : null;
  const requestedUserId = Number.isFinite(parsedUserId) ? parsedUserId : null;
  const isInvalidUserId = Boolean(userId) && requestedUserId === null;
  const isOwnProfile = !userId || requestedUserId === user?.id;
  const displayName = publicUserName(profile || user, DEFAULT_USER_NAME);
  const profileUsername = safeUsername(profile);
  const accountName = profileUsername ? `@${profileUsername}` : 'Ник не указан';
  const recentRooms = Array.isArray(profile?.recentRooms) ? profile.recentRooms : [];
  const formErrors = useMemo(() => validateForm(form), [form]);
  const canSubmit = editing && Object.keys(formErrors).length === 0 && !saving;

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setSuccess('');
    if (isInvalidUserId) {
      setProfile(null);
      setLoadError('Некорректная ссылка на профиль');
      setLoading(false);
      return;
    }

    try {
      const data = isOwnProfile
        ? await getCurrentUserProfile()
        : await getUserProfile(requestedUserId);
      setProfile(data);
      setForm(normalizeForm(data));
      setEditing(false);
    } catch (error) {
      setProfile(null);
      setLoadError(messageFromError(error, LOAD_ERROR_MESSAGE));
    } finally {
      setLoading(false);
    }
  }, [isInvalidUserId, isOwnProfile, requestedUserId]);

  useEffect(() => {
    document.title = isOwnProfile ? 'BetMate — Профиль' : 'BetMate — Профиль пользователя';
  }, [isOwnProfile]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEdit = () => {
    setForm(normalizeForm(profile));
    setSaveError('');
    setSuccess('');
    setEditing(true);
  };

  const handleCancel = () => {
    setForm(normalizeForm(profile));
    setSaveError('');
    setEditing(false);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaveError('');
    setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setSaveError('');
    setSuccess('');
    try {
      const updatedUser = await updateCurrentUser({
        displayName: form.displayName,
        username: form.username,
        photoUrl: form.photoUrl,
      });
      setUser?.(updatedUser);
      await refreshCurrentUser?.();
      const updatedProfile = await getCurrentUserProfile();
      setProfile(updatedProfile);
      setForm(normalizeForm(updatedProfile));
      setEditing(false);
      setSuccess(EDIT_SUCCESS_MESSAGE);
    } catch (error) {
      setSaveError(messageFromError(error, SAVE_ERROR_MESSAGE));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bm-animate-in" style={{ maxWidth: 900 }}>
        <SkeletonList count={3} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)', maxWidth: 720 }}>
        <h1 className="bm-h2">Профиль</h1>
        <Card>
          <EmptyState
            icon={<PiUserCircleDuotone size={36} />}
            title="Профиль недоступен"
            hint={loadError}
            actionLabel="Повторить"
            onAction={loadProfile}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)', maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="bm-h2">{isOwnProfile ? 'Мой профиль' : 'Профиль пользователя'}</h1>
          <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
            {DEFAULT_ROOM_LIMIT_TEXT}
          </p>
        </div>
        {isOwnProfile && (
          <Button
            variant="danger"
            size="sm"
            icon={<PiSignOutDuotone size={16} />}
            onClick={handleLogout}
          >
            Выйти
          </Button>
        )}
      </div>

      <Card variant="glass" style={{ display: 'flex', alignItems: 'center', gap: 'var(--bm-space-lg)', flexWrap: 'wrap' }}>
        <Avatar photoUrl={profile?.photoUrl} displayName={displayName} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="bm-h3">{displayName}</h2>
          <p className="bm-text-secondary bm-text-sm bm-truncate" style={{ marginTop: 6 }}>
            {accountName}
          </p>
        </div>
        {isOwnProfile && !editing && (
          <Button
            variant="primary"
            size="sm"
            icon={<PiPencilSimpleDuotone size={16} />}
            onClick={handleEdit}
          >
            Редактировать
          </Button>
        )}
      </Card>

      {success && <div className="bm-auth-success">{success}</div>}

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--bm-space-md)' }}>
        <StatCard
          icon={<PiSwordDuotone size={22} />}
          value={profile?.challengesCount ?? 0}
label="Пари"
        />
        <StatCard
          icon={<PiTrophyDuotone size={22} />}
          value={profile?.winsCount ?? 0}
          label="Побед"
          iconColor="var(--bm-accent-gold)"
          iconBg="rgba(255, 209, 102, 0.12)"
        />
        <StatCard
          icon={<PiUserDuotone size={22} />}
          value={profile?.totalScore ?? 0}
          label="Очков"
          iconColor="var(--bm-accent-green)"
          iconBg="rgba(0, 214, 143, 0.12)"
        />
        <StatCard
          icon={<PiCalendarDuotone size={22} />}
          value={recentRooms.length}
          label="Последних комнат"
        />
      </section>

      {isOwnProfile && editing && (
        <Card>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
            <h2 className="bm-h3">Редактирование профиля</h2>
            <Input
              label="Отображаемое имя"
              value={form.displayName}
              maxLength={DISPLAY_NAME_MAX_LENGTH}
              error={formErrors.displayName}
              onChange={(event) => handleFormChange('displayName', event.target.value)}
            />
            <Input
              label="Ник"
              value={form.username}
              maxLength={USERNAME_MAX_LENGTH}
              error={formErrors.username}
              onChange={(event) => handleFormChange('username', event.target.value)}
            />
            <Input
              label="Ссылка на аватар"
              value={form.photoUrl}
              maxLength={PHOTO_URL_MAX_LENGTH}
              icon={<PiImageDuotone size={16} />}
              error={formErrors.photoUrl}
              placeholder="https://example.com/avatar.png"
              onChange={(event) => handleFormChange('photoUrl', event.target.value)}
            />
            {saveError && <div className="bm-auth-error">{saveError}</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button
                type="submit"
                variant="success"
                loading={saving}
                disabled={!canSubmit}
                icon={<PiFloppyDiskDuotone size={16} />}
              >
                Сохранить
              </Button>
              <Button variant="secondary" disabled={saving} onClick={handleCancel}>
                Отменить
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <h3 className="bm-h4" style={{ marginBottom: 'var(--bm-space-md)' }}>Данные аккаунта</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--bm-space-md)' }}>
          <ProfileField label="Отображаемое имя" value={profile?.displayName || 'Не указано'} />
          <ProfileField label="Ник" value={accountName} />
          <ProfileField label="ID пользователя" value={profile?.userId} />
        </div>
      </Card>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
        <h2 className="bm-h3">Последние комнаты</h2>
        {recentRooms.length > 0 ? (
          <div style={{ display: 'grid', gap: 'var(--bm-space-md)' }}>
            {recentRooms.map((room) => (
              <Card key={room.id} variant="interactive" onClick={() => navigate('/rooms')}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <h3 className="bm-h4">{room.name}</h3>
                    {room.description && (
                      <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
                        {room.description}
                      </p>
                    )}
                  </div>
                  <span className="bm-text-secondary bm-text-sm">
                    {formatDate(room.joinedAt) || 'Дата вступления не указана'}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <EmptyState
              icon={<PiUserCircleDuotone size={36} />}
              title="Комнат пока нет"
              hint="Когда пользователь вступит в комнаты, они появятся здесь."
            />
          </Card>
        )}
      </section>
    </div>
  );
};

// ===================================================================================================================
// = Implementation
// ===================================================================================================================

const validateForm = (form) => {
  const errors = {};
  if (!form.displayName.trim()) {
    errors.displayName = DISPLAY_NAME_REQUIRED_MESSAGE;
  }
  if (!form.username.trim()) {
    errors.username = USERNAME_REQUIRED_MESSAGE;
  }
  if (form.photoUrl.length > PHOTO_URL_MAX_LENGTH) {
    errors.photoUrl = `Ссылка не должна быть длиннее ${PHOTO_URL_MAX_LENGTH} символов`;
  }
  return errors;
};

const Avatar = ({ photoUrl, displayName }) => {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt="Аватар пользователя"
        style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          boxShadow: 'var(--bm-shadow-glow)',
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, var(--bm-accent-primary), var(--bm-accent-hover))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 'var(--bm-text-xl)',
        fontWeight: 800,
        color: '#fff',
        flexShrink: 0,
        boxShadow: 'var(--bm-shadow-glow)',
      }}
    >
      {getInitials(displayName)}
    </div>
  );
};

const ProfileField = ({ label, value }) => (
  <div>
    <div className="bm-text-sm bm-text-secondary">{label}</div>
    <div className="bm-font-medium bm-truncate">{value || '—'}</div>
  </div>
);

export default ProfilePage;
